/**
 * @deprecated Use rate override helpers from `domains-billing-calculation` instead.
 */
import BigNumber from 'bignumber.js';
import {
  getCurrencyPrecision,
  type SupportedPaymentCurrency,
} from 'domains-billing-currency';
import { AdminFeeAmountType } from 'application-types';

import {
  MarkupAmountType,
  ProductItem,
  SellingPrice,
} from '../../billingPayments/types';

const PERCENTAGE_DIVISOR = 100;

type Markup = {
  value?: number;
  markupAmountType?: MarkupAmountType;
};

type AdminFee = {
  value?: number;
  adminFeeAmountType?: AdminFeeAmountType;
};

type OverridePricing = Required<SellingPrice>;

type NormalizeOverrideSellingPriceParams = {
  amount: number;
  pricing: OverridePricing;
  userInputRate?: number;
};

const roundToTwoDecimals = (value: number): number => {
  return Math.round(value * 100) / 100;
};

const isFixedMarkup = (markup?: Markup): boolean => {
  return markup?.markupAmountType === MarkupAmountType.MarkupAmountTypeFixed;
};

const isFixedAdminFee = (adminFee?: AdminFee): boolean => {
  return (
    adminFee?.adminFeeAmountType === AdminFeeAmountType.AdminFeeAmountTypeFixed
  );
};

const getFixedAmount = (
  rate?: Markup | AdminFee,
  isFixed = false
): BigNumber => {
  return isFixed ? new BigNumber(rate?.value || 0) : new BigNumber(0);
};

const getPercentageRate = (
  rate?: Markup | AdminFee,
  isFixed = false
): BigNumber => {
  return isFixed
    ? new BigNumber(0)
    : new BigNumber(rate?.value || 0).dividedBy(PERCENTAGE_DIVISOR);
};

export const getMinimumRate = (item?: ProductItem | null): number => {
  if (!item) return 0;

  let minRate = 0;
  const { markup, adminFee } = item.product;

  if (markup && isFixedMarkup(markup)) {
    minRate += markup.value || 0;
  }

  if (adminFee && isFixedAdminFee(adminFee)) {
    minRate += adminFee.value || 0;
  }

  return minRate;
};

export const calculateNewAmountFromRate = (
  displayRate: number,
  markup?: Markup,
  currency?: SupportedPaymentCurrency,
  adminFee?: AdminFee
): number => {
  const fixedMarkup = isFixedMarkup(markup);
  const fixedAdminFee = isFixedAdminFee(adminFee);
  const fixedAmount = getFixedAmount(markup, fixedMarkup).plus(
    getFixedAmount(adminFee, fixedAdminFee)
  );
  const percentageRate = getPercentageRate(markup, fixedMarkup).plus(
    getPercentageRate(adminFee, fixedAdminFee)
  );

  const newAmount = new BigNumber(displayRate)
    .minus(fixedAmount)
    .dividedBy(new BigNumber(1).plus(percentageRate));

  const precision = currency ? getCurrencyPrecision(currency) : 2;

  return newAmount.decimalPlaces(precision).toNumber();
};

export const normalizeOverrideSellingPrice = ({
  amount,
  pricing,
  userInputRate,
}: NormalizeOverrideSellingPriceParams): SellingPrice => {
  const grossTax = roundToTwoDecimals(pricing.grossTax);
  const grossAdminFee = roundToTwoDecimals(pricing.grossAdminFee);
  const grossAdminFeeTax = roundToTwoDecimals(pricing.grossAdminFeeTax);
  const listPrice =
    userInputRate !== undefined
      ? roundToTwoDecimals(userInputRate)
      : roundToTwoDecimals(pricing.listPrice);
  const grossMarkup =
    userInputRate !== undefined
      ? roundToTwoDecimals(
          listPrice - roundToTwoDecimals(amount) - grossAdminFee
        )
      : roundToTwoDecimals(pricing.grossMarkup);
  const netPrice =
    userInputRate !== undefined
      ? roundToTwoDecimals(listPrice + grossTax + grossAdminFeeTax)
      : roundToTwoDecimals(pricing.netPrice);

  return {
    grossTax,
    grossMarkup,
    listPrice,
    netPrice,
    grossAdminFee,
    grossAdminFeeTax,
  };
};

export const calculateListPriceFromAmount = (
  amount: number,
  markup?: Markup,
  adminFee?: AdminFee
): number => {
  const baseAmount = new BigNumber(amount);
  const markupAmount = isFixedMarkup(markup)
    ? new BigNumber(markup?.value || 0)
    : baseAmount.multipliedBy(markup?.value || 0).dividedBy(PERCENTAGE_DIVISOR);
  const adminFeeAmount = isFixedAdminFee(adminFee)
    ? new BigNumber(adminFee?.value || 0)
    : baseAmount
        .multipliedBy(adminFee?.value || 0)
        .dividedBy(PERCENTAGE_DIVISOR);
  const listPrice = baseAmount.plus(markupAmount).plus(adminFeeAmount);

  return listPrice.toNumber();
};

export const getOriginalListPrice = (
  item?: ProductItem | null,
  currency?: SupportedPaymentCurrency
): number => {
  if (!item?.product.originalPrice) return 0;

  const listPrice = calculateListPriceFromAmount(
    item.product.originalPrice.amount,
    item.product.originalPrice.markup || undefined,
    item.product.originalPrice.adminFee || undefined
  );

  const precision = currency ? getCurrencyPrecision(currency) : 2;

  return Number(
    new BigNumber(listPrice).toFixed(precision, BigNumber.ROUND_DOWN)
  );
};

export const getCurrentListPrice = (item?: ProductItem | null): number => {
  if (item?.isRateOverridden && item?.userInputRate !== undefined) {
    return item.userInputRate;
  }

  return item?.product.sellingPrice?.listPrice || 0;
};
